import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { IUserDetails } from '../../../shared/services/user-details/user-details.model'
import UserDetailsService from '../../../shared/services/user-details/user-details.service'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display'
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import { InviteUserDialog } from '../../dialogs/settings/users/invite-user-dialog'
import { IInviteUserDetails, IRole } from '../../../shared/services/users-teams/users-teams.model'
import UsersTeamService from '../../../shared/services/users-teams/users-teams.service'
import gaiaTheme from '../../../root-components/theme'
import { DeactivateUserDialog } from '../../dialogs/settings/users/deactivate-user-dialog'
import { SettingsMobileNavbar } from '../../../root-components/navigation/settings-mobile-navbar'
import { LoadingPage } from '../loading-page'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model'
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels'
import { useNavigate } from 'react-router-dom'

export const SettingsUsersPage = () => {
  //Services
  const userDetailsService = new UserDetailsService();
  const usersTeamsService = new UsersTeamService();
  const subsidiaryService = SubsidiaryService.getInstance()
  //Context
  const userNotificationsContext = React.useContext(UserNotificationsContext);
  const userDetailsContext = React.useContext(UserDetailsContext);
  const userDetails = userDetailsContext.userDetails
  const screenLabelsContext = useContext(ScreenLabelsContext)
  //State
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
  const [users, setUsers] = React.useState<IUserDetails[]>([]);
  const [allActiveUsers, setAllActiveUsers] = useState<IUserDetails[]>()
  const [roles, setRoles] = React.useState<IRole[]>();
  const [inviteUserDetails, setInviteUserDetails] = React.useState<IInviteUserDetails>({
    id: '',
    firstName: '',
    surname: '',
    email: '',
    roles: [],
  })
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string>()
  const [paramString, setParamString] = React.useState<string>('active');
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [openInviteDialog, setOpenInviteDialog] = React.useState<boolean>(false);
  const [openDeactivateDialog, setOpenDeactivateDialog] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmiting] = useState<boolean>(false)
  //Layout
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const navigate = useNavigate()

  //Use Effects
  useEffect(() => {
    getUsers();
    setSelected([])
  }, [change, inputPage, perPage])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [paramString, order, orderBy])

  useEffect(() => {
    usersTeamsService.getAllRoles()
      .then(response => {
        setRoles(response)
      })
      .catch(err =>
        console.log(err)
      )

    subsidiaryService.getAll()
      .then(response => {
        setSubsidiaries(response)
      })
      .catch(err => console.log(err))

    getAllActiveUsers()
  }, [])

  const getUsers = () => {
    userDetailsService.getAllUserDetailsWithPagination(paramString, inputPage, perPage, orderBy, order)
      .then((response) => {
        setTotalRecords(response.count);
        setIsLoading(false)
        setUsers(response.rows)
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
    userDetailsContext.forceUpdateUserDetails()
  }

  const getAllActiveUsers = () => {
    userDetailsService.getAllUserDetails('?status=active')
      .then(response => {
        const activeAdminsOrAnalysts = response.filter(user => user.roles.includes('admin') || user.roles.includes('analyst'))
        setAllActiveUsers(activeAdminsOrAnalysts)
      })
      .catch(err => console.log(err))
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: paramString === 'inactive' ? 'Reactivate User' : 'Actions', sortable: false, includeInMobile: paramString === 'invited' },
    { id: 'firstName', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'email', numeric: false, label: 'Email', sortable: true },
    { id: 'role', numeric: false, label: 'Role', sortable: false },
    { id: 'defaultSubsidiary', numeric: false, label: `${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`, sortable: false },
  ];

  const rows = users.map(user => {
    return {
      id: user.id,
      firstName: user.firstName + ' ' + user.surname,
      email: user.email,
      role: (
        <Stack direction={isMobileModeMd ? 'column' : "row"} spacing={1}>
          {user.roles.map(role => (
            <Typography
              key={role}
              sx={{
                width: '75px',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'lightGrey.dark',
                borderRadius: '20px',
                p: 0.5,
                textAlign: 'center',
                fontSize: '0.82rem',
                color: 'black'
              }}>
              {role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()}
            </Typography>
          ))}
        </Stack>
      ),
      defaultSubsidiary: user.defaultSubsidiaryId ? subsidiaries?.find(subsidiary => subsidiary.id === user.defaultSubsidiaryId)?.name : '',
      action: (
        paramString === 'active' ? (
          <TableActionButtons
            id={user.id}
            includeView={true}
            includeEdit={true}
            includeDelete={true}
            preferViewThenAdditionalThenDelete={true}
            onView={() => navigate(`/settings/company-settings/users/${user.id}`, { state: { user } })}
            onEdit={() => editClickHandler(user.id)}
            onDelete={() => submitDisableHandler(user.id, user.hasOutstandingActions)}
          />
        ) : paramString === 'inactive' ? (
          <TableActionButtons
            id={user.id}
            includeOther={true}
            otherButtonIcon={IconName.Autorenew}
            otherButtonText={'Reactivate'}
            onOther={() => submitEnableHandler({} as any, user.id)}
          />
        ) : paramString === 'invited' ? (
          <TableActionButtons
            id={user.id}
            includeOther={true}
            otherButtonIcon={IconName.EmailOutlined}
            otherButtonText='Resend Invite Link'
            onOther={() => resendInvitedUserLinkHandler(user.id)}
          />
        ) : null
      )
    }
  });

  // Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const editClickHandler = (userId: string) => {
    const selectedUser = users.find(user => user.id === userId)
    if (selectedUser) {
      setInviteUserDetails(selectedUser)
      setOpenInviteDialog(true)
      setIsEdit(true)
    }
  }

  const submitDisableHandler = (userId: string, hasOutstandingActions: boolean) => {
    if (userDetails?.id === userId) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: 'You are unable to deactivate your own account.', buttonCopy: 'Ok', hideCancelButton: true })
    } else if (userDetails?.id !== userId && !hasOutstandingActions) {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: 'Are you sure you want to deactivate this user?', buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            disableUser(userId, null)
          }
        })
        .catch(err => console.log(err))
    }
    else if (userDetails?.id !== userId && hasOutstandingActions) {
      setSelectedUserId(userId)
      setOpenDeactivateDialog(true)
    }

  }

  const submitEnableHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: string) => {
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: 'Are you sure you want to reactivate this user?', buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        setSelected([])
        setAnchorEl(null);
        if (response) {
          enableUser(userId);
        }
      })
  }

  const resendInvitedUserLinkHandler = (userId: string) => {
    userDetailsService.resendInvitedUserLink(userId)
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Sent successfully' });
      })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        console.log(err)
      })
  }

  const enableUser = (userId: string) => {
    userDetailsService.enableUser({ userId: userId })
      .then(response => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Enabled successfully' });
        if (inputPage > 0 && users && users.length === 1) {
          setInputPage(inputPage - 1)
        } else {
          getUsers()
        }
        getAllActiveUsers()
      })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        console.log(err)
      })
  }


  //Deactivate User Dialog
  const disableUser = (userId: string, newUserId: string | null) => {
    if (newUserId) {
      if (!isDialogSubmitting) {
        setIsDialogSubmiting(true)
        userDetailsService.disableUser(userId, { newAssignedUserId: newUserId })
          .then(response => {
            if (inputPage > 0 && users && users.length === 1) {
              setInputPage(inputPage - 1)
            } else {
              getUsers()
            }
            userNotificationsContext.openSnackbar({ open: true, message: 'Disabled successfully' });
            setIsDialogSubmiting(false)
            setOpenDeactivateDialog(false)
            getAllActiveUsers()
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            console.log(err)
            setIsDialogSubmiting(false)
          })
      }
    } else {
      if (!isDialogSubmitting) {
        setIsDialogSubmiting(true)
        userDetailsService.disableUser(userId)
          .then(response => {
            if (inputPage > 0 && users && users.length === 1) {
              setInputPage(inputPage - 1)
            } else {
              getUsers()
            }
            setOpenDeactivateDialog(false)
            getAllActiveUsers()
            setIsDialogSubmiting(false)
            userNotificationsContext.openSnackbar({ open: true, message: 'Disabled successfully' });
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            console.log(err)
            setIsDialogSubmiting(false)

          })
      }
    }
  }

  const closeDeactivateDialogHandler = () => {
    setOpenDeactivateDialog(false);
    setSelectedUserId('')
  };

  //Invite User Dialog
  const closeInviteDialogHandler = () => {
    setOpenInviteDialog(false);
    setInviteUserDetails({
      id: '',
      firstName: '',
      surname: '',
      email: '',
      roles: [],
    })
    setIsEdit(false)
  };

  // Component Event Handlers
  const toggleHandler = (e: React.MouseEvent<HTMLElement>, newTable: string | null) => {
    if (newTable !== null) {
      setParamString(newTable)
    }
    setIsLoading(true)
  };

  const openInviteDialogHandler = () => {
    setOpenInviteDialog(true);
  };

  return (
    <PageOuterContainer>
      {isMobileModeSm &&
        <Box sx={{ pb: 4 }}>
          <SettingsMobileNavbar />
        </Box>}
      <PageHeading
        title='Users'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={openInviteDialogHandler}
        actionButtonText='Invite User'
        hasTabs={false}
      />
      <Stack flexDirection={isMobileModeMd ? 'column' : 'row'} justifyContent={'space-between'} sx={{ mb: isMobileModeMd ? 2 : 4 }}>
        <Stack sx={{ mb: isMobileModeMd ? 2 : 0 }}>
          <ToggleButtonGroup value={paramString} onChange={toggleHandler} exclusive aria-label='table' orientation={isMobileModeMd ? "vertical" : 'horizontal'}>
            <ToggleButton value='active' aria-label='active users' sx={{ width: !isMobileModeMd ? '175px' : '100%', pt: 0.75, pb: 0.75 }}> <Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Active Users</Typography></ToggleButton>
            <ToggleButton value='invited' aria-label='invited users' sx={{ width: !isMobileModeMd ? '175px' : '100%', pt: 0.75, pb: 0.75 }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Invited Users </Typography> </ToggleButton>
            <ToggleButton value='inactive' aria-label='deactivated users' sx={{ width: !isMobileModeMd ? '175px' : '100%', pt: 0.75, pb: 0.75 }}> <Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}> Deactivated Users</Typography> </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack >
      <LoadingPage pageReady={!isLoading}>
        {users &&
          <TableDisplay
            onOrderChange={handleOrderChange}
            isMobileMode={isMobileModeMd}
            headCells={headCells}
            rows={rows}
            haveCheckboxSelect={false}
            selected={selected}
            setSelected={setSelected}
            selectByIcon={true}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage} />}
      </LoadingPage>
      {roles && openInviteDialog &&
        <InviteUserDialog
          roles={roles}
          open={openInviteDialog}
          closeHandler={closeInviteDialogHandler}
          dialogDetails={inviteUserDetails}
          setSelectedRoles={setSelectedRoles}
          selectedRoles={selectedRoles}
          getUsers={getUsers}
          isEdit={isEdit}
        />
      }
      {allActiveUsers && selectedUserId && openDeactivateDialog &&
        <DeactivateUserDialog
          open={openDeactivateDialog}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDeactivateDialogHandler}
          userId={selectedUserId}
          submitHandler={disableUser}
          users={allActiveUsers}
        />}
    </PageOuterContainer>

  )
}